import Api from "../../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../../core/data/redux/apis/urls";

export interface LoanTypeOption {
  label: string;
  value: string;
}

export interface SuretyPerson {
  empId: string;
  name: string;
}

export interface LoanSuretyItem {
  id: string;
  empId: string;
  empName: string;
  loanType: string;
  loanText: string;
  firstSurety: SuretyPerson;
  secondSurety: SuretyPerson;
  status: string;
  [key: string]: any;
}

function unwrapList(res: any): any[] {
  const root = res?.data ?? res;
  const responseData = root?.data ?? root;
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.Loan_Types)) return responseData.Loan_Types;
  if (Array.isArray(root)) return root;
  return [];
}

function toError(err: any, fallback: string): Error {
  const msg =
    err?.response?.data?.data?.message ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;
  return new Error(typeof msg === "string" ? msg : fallback);
}

export async function fetchLoanTypes(): Promise<LoanTypeOption[]> {
  try {
    const res: any = await Api.post(API_URLS.LOAN_SURETY.LOAN_TYPES, {
      action: "LoanSuretyLoanTypes",
    });

    const items = unwrapList(res);
    return items
      .map((item: any) => ({
        label: item.text || item.type_text || item.label || "",
        value: String(item.value || item.type_id || item.id || ""),
      }))
      .filter((item: LoanTypeOption) => item.value);
  } catch (err: any) {
    throw toError(err, "Failed to load loan types");
  }
}

function mapSuretyItem(item: any, index: number): LoanSuretyItem {
  return {
    id: String(item.empId || item.EmpID || index),
    empId: item.empId || item.EmpID || "",
    empName: item.empName || item.EmpName || "",
    loanType: item.loanType || item.LoanType || "",
    loanText: item.loanText || item.LoanText || "",
    firstSurety: {
      empId: item.firstSurety?.empId || item.EmpIDOfFirstSurety || "",
      name: item.firstSurety?.name || item.NameOfEmpIDOfFirstSurety || "",
    },
    secondSurety: {
      empId: item.secondSurety?.empId || item.EmpIDOfSecondSurety || "",
      name: item.secondSurety?.name || item.NameOfEmpIDOfSecondSurety || "",
    },
    status: item.status || item.Status || "approved",
  };
}

/** Same payload shape as the mobile app: nested Request.data.loanType */
export async function fetchSuretyReceived(
  loanType: string
): Promise<LoanSuretyItem[]> {
  try {
    const res: any = await Api.post(API_URLS.LOAN_SURETY.RECEIVED, {
      Request: {
        data: {
          loanType,
        },
      },
      action: "LoanSuretyReceived",
    });
    return unwrapList(res).map(mapSuretyItem);
  } catch (err: any) {
    throw toError(err, "Failed to fetch surety received data");
  }
}

export async function fetchSuretyGiven(
  loanType: string
): Promise<LoanSuretyItem[]> {
  try {
    const res: any = await Api.post(API_URLS.LOAN_SURETY.GIVEN, {
      loanType,
      action: "LoanSuretyGiven",
    });
    return unwrapList(res).map(mapSuretyItem);
  } catch (err: any) {
    throw toError(err, "Failed to fetch surety given data");
  }
}
